import { inject, watchEffect } from 'vue';
import { toValue } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import LinkedList from '@/shared/services/linked-list';
import useApi from '@/shared/composables/useApi';
import useAssets from '@/shared/composables/useAssets';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useTerms from '@/shared/composables/useTerms';
import { validateAttendance } from '@/shared/services/attendance-validation';

export default function useAttendance(_subsidyProgramId) {
  const $bct = inject('translator');

  const ATTENDANCE_LOG_HOURS_FIELDS = [
    {
      text: 'Hours attended',
      value: 'hours',
      type: 'number',
      required: true,
      max: 24,
      min: 0,
    },
  ];

  const { api: Api, scopedApi } = useApi();
  const { assets } = useAssets();
  const { smAndDown } = useDisplay();
  const route = useRoute();
  const store = useStore();
  const { terms } = useTerms();
  const { updateQuery } = useRouterHelper();

  const attendanceLogFilters = ref({});
  const attendanceLogStatusDialog = ref(null);
  const attendanceLogQuestionDialog = ref(null);
  const attendanceLogHoursDialog = ref(null);
  const operatingLogStatusDialog = ref(null);
  const attendanceLogs = ref([]);
  const attendanceLogsMonthFilters = ref([]);
  const attendanceLogQuestions = ref([]);
  const attendanceTotals = ref({});
  const errors = ref([]);
  const processing = ref(false);
  const providerId = ref(null);
  const operatingLogs = ref([]);
  const queuedAttendanceLogQuestionId = ref(null);
  const queuedAttendanceLogAnswers = ref([]);
  const queuedOperatingLogChanges = ref([]);
  const attendanceStatuses = computed(() => subsidyProgram.value?.attendance_statuses ?? []);
  const operatingStatuses = computed(() => subsidyProgram.value?.operating_statuses ?? []);

  const presentStatusCodes = computed(() =>
    attendanceStatuses.value.filter((status) => !status.absent).map((status) => status.code),
  );
  const providers = ref([]);
  const programs = ref([]);
  const queuedAttendanceLogChanges = ref([]);
  const selectedOperatingLog = ref(null);
  const selectedProgram = ref(null);
  const showAttendanceLogChangeQueue = ref(false);
  const showAttendanceLogQuestionQueue = ref(false);
  const showOperatingLogChangeQueue = ref(false);
  const dailyTotals = ref({});
  const attendanceLogsCurrentPage = ref(1);
  const attendanceLogsPageSize = ref(smAndDown.value ? 10 : 20);
  const attendanceLogsTotalItemsCount = ref(1);
  const attendanceLogsTotalPages = ref(1);
  const subsidyProgram = ref(null);

  const attendanceLink = computed(() => {
    if (!selectedOperatingLog.value) return null;

    const params = getAttendanceLogParams();
    const filename = buildAttendanceDownloadFilename(params);

    return Api.shared.attendanceLog.downloadUrl({ filename, ...params });
  });

  const attendanceLogSchema = computed(() => {
    return Object.values(store.state.schemas).find(
      (schema) =>
        schema.data_type === 'AttendanceLog' && schema.id === attendanceLogs.value[0]?.schema_id,
    );
  });

  const currentSubmittedFilter = computed(
    () => attendanceLogFilters.value['filters[submitted]'] || [],
  );

  const operatingLogActionItems = computed(() => {
    return operatingLogs.value.map((ol) => ({
      event: 'change',
      operatingLogId: ol.id,
      title: [$bct(assets.months[ol.month]), ol.year].join(' '),
    }));
  });

  const operatingLogLink = computed(() => {
    if (!selectedOperatingLog.value) return null;

    const programName = programs.value.find(
      (program) => program.id === selectedOperatingLog.value.program_id,
    )?.name;
    const filename = `${programName} ${terms.value.attendance} for ${selectedOperatingLog.value.year}-${selectedOperatingLog.value.month}`;
    return Api.shared.operatingLog.downloadUrl(selectedOperatingLog.value.id, filename);
  });

  const operatingLogSchema = computed(() =>
    Object.values(store.state.schemas).find(
      (schema) =>
        schema.data_type === 'OperatingLog' && schema.id === selectedOperatingLog.value?.schema_id,
    ),
  );

  const monthFilterSelections = computed(() => {
    return attendanceLogFilters.value['filters[month_year]'] ?? [];
  });

  const programActionItems = computed(() => {
    return programs.value.map((program) => ({
      event: 'change',
      title: program.name,
      programId: program.id,
    }));
  });

  const selectedMonth = computed(() => {
    if (selectedOperatingLog.value) {
      return [
        $bct(assets.months[selectedOperatingLog.value.month]),
        selectedOperatingLog.value.year,
      ].join(' ');
    }
    return null;
  });

  const subsidyProgramId = computed(() => toValue(_subsidyProgramId));

  function calculateAttendanceTotals() {
    let localTotals = {};
    attendanceLogs.value.forEach((al) => {
      let total = 0;
      for (let i = 1; i <= 31; i++) {
        if (wasPresentOnDay(al, i)) {
          total += 1;
        }
      }
      localTotals[al.id] = total;
    });
    attendanceTotals.value = localTotals;
  }

  function calculateDailyTotals() {
    for (let i = 1; i <= 31; i++) {
      if (dailyTotals.value[i]) {
        dailyTotals.value[i] = 0;
      }
    }

    attendanceLogs.value.forEach((al) => {
      for (let i = 1; i <= 31; i++) {
        dailyTotals.value[i] = dailyTotals.value[i] || 0;
        if (wasPresentOnDay(al, i)) {
          dailyTotals.value[i] += 1;
        }
      }
    });
  }

  async function changeOperatingLog(event) {
    const operatingLog = operatingLogs.value.find((ol) => ol.id === event.operatingLogId);
    if (!operatingLog) return;

    selectedOperatingLog.value = operatingLog;
    await updateQuery({ operatingLogId: operatingLog.id });
    await loadAttendanceLogsMonthFilters();
    await loadAttendanceLogs();
  }

  async function changeProgram(evt) {
    if (selectedProgram.value !== null) {
      attendanceLogFilters.value = {};
    }

    selectedProgram.value = programs.value.find((program) => program.id === evt.programId);
    selectedOperatingLog.value = null;
    attendanceLogs.value = [];
    attendanceLogsMonthFilters.value = [];
    operatingLogs.value = [];
    await updateQuery({ programId: selectedProgram.value.id });
    await loadOperatingLogs();
  }

  async function changeProvider(evt) {
    providerId.value = evt.providerId;
    await loadPrograms();
  }

  async function clearAttendanceLog() {
    if (!confirm($bct('Are you sure you want to clear the selected attendance records?'))) return;

    await updateAttendanceLogsStatus(null);
  }

  async function clearOperatingLog() {
    if (!confirm($bct('Are you sure you want to clear the selected days?'))) return;

    await updateOperatingLog(null);
  }

  function clearAttendanceChangeQueue() {
    queuedAttendanceLogChanges.value = [];
    showAttendanceLogChangeQueue.value = false;
  }

  function clearOperatingLogChangeQueue() {
    queuedOperatingLogChanges.value = [];
    showOperatingLogChangeQueue.value = false;
  }

  function clearAttendanceLogQuestionQueue() {
    queuedAttendanceLogAnswers.value = [];
    queuedAttendanceLogQuestionId.value = null;
    showAttendanceLogQuestionQueue.value = false;
  }

  function buildMonthYears(params) {
    const monthYears =
      params['filters[month_year]'] ??
      attendanceLogsMonthFilters.value.map((filter) => ({
        month: filter.value.month,
        year: filter.value.year,
      }));

    return [...new Set(monthYears.map((item) => `${item.year}-${item.month}`))];
  }

  function buildAttendanceDownloadFilename(params) {
    const programName = programs.value.find(
      (program) => program.id === selectedOperatingLog.value.program_id,
    )?.name;

    const monthYears = buildMonthYears(params);
    const years = [...new Set(monthYears.map((monthYear) => monthYear.split('-')[0]).sort())];

    const yearLabel = years.length
      ? `${years[0]}${years.length > 1 ? `-${years[years.length - 1]}` : ''}`
      : selectedOperatingLog.value.year;

    const monthLabel = !monthYears.length
      ? `-${selectedOperatingLog.value.month}`
      : monthYears.length === 1
        ? `-${monthYears[0].split('-')[1]}`
        : '';

    return `${programName} ${terms.value.attendance} for ${yearLabel}${monthLabel}`;
  }

  function getAttendanceLogParams() {
    return {
      operating_log_id: selectedOperatingLog.value?.id,
      program_id: selectedProgram.value.id,
      subsidy_program_id: subsidyProgramId.value ?? route.query.subsidyProgramId,
      page: attendanceLogsCurrentPage.value,
      page_size: attendanceLogsPageSize.value,
      ...attendanceLogFilters.value,
    };
  }

  function handleEditAttendanceLogStatus() {
    if (attendanceLogSchema.value.meta.enable_hourly_attendance) {
      attendanceLogHoursDialog.value.open();
    } else {
      attendanceLogStatusDialog.value.open();
    }
  }

  async function handleOpenOperatingLog(operatingLogEditorRef) {
    if (!operatingLogEditorRef) {
      throw new Error('OperatingLogEditor ref is required to open the operating log');
    }
    return operatingLogEditorRef.open(selectedOperatingLog.value);
  }

  async function loadAttendanceLogs() {
    if (!selectedProgram.value) return;

    processing.value = true;

    const response = await Api.shared.attendanceLog.index({
      operating_log_id: selectedOperatingLog.value?.id,
      program_id: selectedProgram.value.id,
      subsidy_program_id: subsidyProgramId.value ?? route.query.subsidyProgramId,
      page: attendanceLogsCurrentPage.value,
      page_size: attendanceLogsPageSize.value,
      ...attendanceLogFilters.value,
    });

    attendanceLogsTotalPages.value = Number(response?.headers?.['x-page-count'] || 1);
    attendanceLogsTotalItemsCount.value = parseInt(response?.headers?.['x-total-count'] || 0, 10);
    attendanceLogs.value = response?.data ?? [];
    calculateDailyTotals();
    calculateAttendanceTotals();

    await loadAttendanceLogQuestions();

    processing.value = false;
  }

  async function loadAttendanceLogsMonthFilters() {
    if (!selectedProgram.value) return;

    const response = await Api.shared.attendanceLog.index({
      operating_log_id: selectedOperatingLog.value?.id,
      program_id: selectedProgram.value.id,
      subsidy_program_id: subsidyProgramId.value,
      'filters[distinct_months]': true,
    });

    attendanceLogsMonthFilters.value = (response?.data ?? []).map((log) => ({
      text: $bct(assets.months[log.month]) + ' ' + log.year,
      value: { month: log.month, year: log.year },
    }));
  }

  async function loadAttendanceLogQuestions() {
    const resp = await Api.publicApi.organization.question.index({
      owner_id: attendanceLogSchema.value?.id,
      owner_type: 'Schema',
    });

    if (resp?.data) {
      attendanceLogQuestions.value = LinkedList.sort(resp.data).filter(
        (question) => question.published,
      );
    }
  }

  async function loadOperatingLogs() {
    if (!selectedProgram.value) return;

    const params = {
      limit: 12,
      program_id: selectedProgram.value.id,
      subsidy_program_id: subsidyProgramId.value,
    };

    const resp = await Api.shared.operatingLog.index(params);

    if (resp?.data) {
      operatingLogs.value = resp.data;
    }

    if (operatingLogs.value?.[0]) {
      selectedOperatingLog.value = operatingLogs.value[0];
      await updateQuery({ operatingLogId: operatingLogs.value[0].id });
      await loadAttendanceLogsMonthFilters();
      await loadAttendanceLogs();
    } else {
      // Clear stale data when switching to a program with no operating logs
      attendanceLogs.value = [];
      attendanceLogsMonthFilters.value = [];
      selectedOperatingLog.value = null;
      await updateQuery({ operatingLogId: null });
    }
  }

  async function loadPrograms() {
    if (!providerId.value) return;

    attendanceLogFilters.value = {};
    attendanceLogs.value = [];
    attendanceLogsMonthFilters.value = [];
    operatingLogs.value = [];
    selectedProgram.value = null;
    selectedOperatingLog.value = null;

    const programFilters = {
      subsidy_program_id: subsidyProgramId.value,
    };

    const response = await Api.program.index(providerId.value, programFilters);
    programs.value = response?.data || [];

    if (programs.value.length === 1) {
      selectedProgram.value = programs.value[0];
      await updateQuery({ programId: selectedProgram.value.id });
      await loadOperatingLogs();
    }
  }

  async function loadProviders() {
    const providerFilters = {
      subsidy_program_id: subsidyProgramId.value,
    };
    const response = await scopedApi.provider.index(providerFilters);

    providers.value = response?.data || [];
    if (providers.value.length === 1) {
      providerId.value = providers.value[0].id;
    }

    await loadPrograms();
  }

  async function loadSubsidyProgram() {
    const actualSubsidyProgramId = subsidyProgramId.value ?? route.query.subsidyProgramId;
    if (!actualSubsidyProgramId) return;

    const resp = await Api.shared.subsidyProgram.get(actualSubsidyProgramId);

    subsidyProgram.value = resp?.data;
  }

  watchEffect(async () => {
    await loadSubsidyProgram();
  });

  function openAttendanceLogQuestionDialog() {
    const question = attendanceLogQuestions.value.find(
      (question) => question.id === queuedAttendanceLogQuestionId.value,
    );
    attendanceLogQuestionDialog.value.open({ question });
  }

  function queueAttendanceLogChange(newVal) {
    if (queuedOperatingLogChanges.value.length === 0) {
      queuedAttendanceLogChanges.value.push(newVal);
      showAttendanceLogChangeQueue.value = true;
    }
  }

  function queueAttendanceLogQuestion(newVal) {
    if (queuedOperatingLogChanges.value.length > 0 || queuedAttendanceLogChanges.value.length > 0)
      return;

    if (
      queuedAttendanceLogQuestionId.value &&
      queuedAttendanceLogQuestionId.value != newVal.questionId
    )
      return;

    queuedAttendanceLogQuestionId.value = newVal.questionId;
    queuedAttendanceLogAnswers.value ||= [];
    queuedAttendanceLogAnswers.value.push(newVal.attendanceLogId);
    showAttendanceLogQuestionQueue.value = true;
  }

  function queueOperatingLogChange(day) {
    if (queuedAttendanceLogChanges.value.length === 0) {
      queuedOperatingLogChanges.value.push(day);
      showOperatingLogChangeQueue.value = true;
    }
  }

  function updateAttendanceLog(newAttendanceLog) {
    attendanceLogs.value = attendanceLogs.value.map((log) => {
      return log.id === newAttendanceLog.id ? newAttendanceLog : log;
    });
  }

  async function updateOperatingLog(status) {
    processing.value = true;
    queuedOperatingLogChanges.value.forEach((day) => {
      selectedOperatingLog.value[`day_${day}`] = status ? status.code : null;
    });
    const { data } = await Api.shared.operatingLog.update(
      selectedOperatingLog.value.id,
      selectedOperatingLog.value,
    );
    selectedOperatingLog.value = data;
    await loadAttendanceLogs();
    operatingLogStatusDialog.value.close();
    processing.value = false;
    showOperatingLogChangeQueue.value = false;
    queuedOperatingLogChanges.value = [];
  }

  async function updateAttendanceLogsHours(newVal) {
    await updateAttendanceLogsStatus(newVal.hours);
  }

  async function updateAttendanceLogsAnswers(answer) {
    errors.value = [];
    processing.value = true;
    await Promise.all(
      queuedAttendanceLogAnswers.value.map(async (attendanceLogId) => {
        await Api.shared.attendanceLog.update(attendanceLogId, answer);
      }),
    );
    attendanceLogQuestionDialog.value.close();
    processing.value = false;
    await loadAttendanceLogs();
    showAttendanceLogQuestionQueue.value = false;
    queuedAttendanceLogAnswers.value = [];
    queuedAttendanceLogQuestionId.value = null;
  }

  async function updateAttendanceLogsStatus(updatedValue) {
    errors.value = [];
    processing.value = true;
    await Promise.all(
      queuedAttendanceLogChanges.value.map(async (ac) => {
        const newVal = {};
        newVal[`day_${ac.day}`] = updatedValue?.code || updatedValue || null;
        await Api.shared.attendanceLog.update(ac.log.id, newVal);
      }),
    );
    attendanceLogHoursDialog.value?.close();
    attendanceLogStatusDialog.value?.close();
    processing.value = false;
    await loadAttendanceLogs();
    showAttendanceLogChangeQueue.value = false;
    queuedAttendanceLogChanges.value = [];
  }

  function reloadAttendanceLogsOnViewportChange() {
    attendanceLogsPageSize.value = smAndDown.value ? 10 : 20;
    void loadAttendanceLogs();
  }

  function wasPresentOnDay(attendanceLog, day) {
    return presentStatusCodes.value.includes(attendanceLog[`day_${day}`]);
  }

  /**
   * Checks if a single attendance log is complete (submitted or all required questions answered)
   * @param {Object} log - The attendance log to validate
   * @param {Array} questions - Array of attendance log questions
   * @param {Boolean} validateDays - Whether to validate daily attendance (default: true)
   * @returns {Boolean} - True if log is submitted or complete (no validation errors)
   */
  function isAttendanceLogComplete(log, questions, validateDays = true) {
    // Submitted logs are always considered complete
    if (log.submitted_at) return true;

    const errors = validateAttendance([log], questions, validateDays);
    return errors.length === 0;
  }

  /**
   * Checks if all attendance logs in an array are complete
   * @param {Array} logs - Array of attendance logs to validate
   * @param {Array} questions - Array of attendance log questions
   * @param {Boolean} validateDays - Whether to validate daily attendance (default: true)
   * @returns {Boolean} - True if all logs are complete
   */
  function areAllAttendanceLogsComplete(logs, questions, validateDays = true) {
    if (logs.length === 0) return false;
    return logs.every((log) => isAttendanceLogComplete(log, questions, validateDays));
  }

  /**
   * Computed property that checks if all current attendance logs are complete
   * Uses the composable's internal state (attendanceLogs, attendanceLogQuestions, operatingLogSchema)
   */
  const allAttendanceLogsComplete = computed(() => {
    return areAllAttendanceLogsComplete(
      attendanceLogs.value,
      attendanceLogQuestions.value,
      subsidyProgram.value?.enable_daily_attendance,
    );
  });

  /**
   * Computed property that checks if all attendance logs are ready for submission
   * Only relevant for non-individual attendance cases
   */
  const allAttendanceLogsReadyForSubmission = computed(() => {
    // if any of the logs are already submitted, the rest will also be submitted.
    if (attendanceLogs.value.some((log) => log.submitted_at)) return false;

    // if any of the logs have errors, return false
    const errors = validateAttendance(
      attendanceLogs.value,
      attendanceLogQuestions.value,
      subsidyProgram.value?.enable_daily_attendance,
    );
    return errors.length === 0;
  });

  async function setAttendanceLogFilters(filter) {
    if (filter && filter.value.length) {
      attendanceLogFilters.value = {
        ...attendanceLogFilters.value,
        [`filters[${filter.key}]`]: filter.value,
      };
    } else {
      delete attendanceLogFilters.value[`filters[${filter.key}]`];
    }
    attendanceLogsCurrentPage.value = 1;

    const monthFilters = attendanceLogFilters.value['filters[month_year]'];
    if (filter.key === 'month_year' && monthFilters?.length === 1) {
      const filteredMonth = monthFilters[0];
      const matchingOperatingLog = operatingLogs.value.find(
        (log) => log.month === filteredMonth.month && log.year === filteredMonth.year,
      );

      if (matchingOperatingLog) {
        await changeOperatingLog({ operatingLogId: matchingOperatingLog.id });
        return;
      }
    }

    await loadAttendanceLogs();
  }

  function navigateAttendanceLogsPageTo(page) {
    attendanceLogsCurrentPage.value = page;
    void loadAttendanceLogs();
  }

  watch(smAndDown, () => reloadAttendanceLogsOnViewportChange());

  return {
    ATTENDANCE_LOG_HOURS_FIELDS,
    allAttendanceLogsComplete,
    allAttendanceLogsReadyForSubmission,
    attendanceLink,
    attendanceStatuses,
    attendanceLogFilters,
    attendanceLogHoursDialog,
    attendanceLogStatusDialog,
    attendanceLogQuestionDialog,
    operatingLogStatusDialog,
    attendanceLogs,
    attendanceLogSchema,
    attendanceLogQuestions,
    attendanceTotals,
    attendanceLogsCurrentPage,
    attendanceLogsPageSize,
    attendanceLogsTotalItemsCount,
    attendanceLogsTotalPages,
    attendanceLogsMonthFilters,
    areAllAttendanceLogsComplete,
    buildAttendanceDownloadFilename,
    buildMonthYears,
    currentSubmittedFilter,
    errors,
    handleEditAttendanceLogStatus,
    handleOpenOperatingLog,
    isAttendanceLogComplete,
    loadPrograms,
    monthFilterSelections,
    operatingLogActionItems,
    operatingLogLink,
    operatingStatuses,
    processing,
    programActionItems,
    providerId,
    operatingLogs,
    operatingLogSchema,
    queuedAttendanceLogQuestionId,
    queuedAttendanceLogAnswers,
    queuedOperatingLogChanges,
    presentStatusCodes,
    providers,
    programs,
    queuedAttendanceLogChanges,
    selectedOperatingLog,
    selectedProgram,
    setAttendanceLogFilters,
    showAttendanceLogChangeQueue,
    showAttendanceLogQuestionQueue,
    showOperatingLogChangeQueue,
    dailyTotals,
    subsidyProgram,
    selectedMonth,
    calculateAttendanceTotals,
    calculateDailyTotals,
    changeOperatingLog,
    changeProgram,
    changeProvider,
    clearAttendanceLog,
    clearOperatingLog,
    clearAttendanceChangeQueue,
    clearOperatingLogChangeQueue,
    clearAttendanceLogQuestionQueue,
    loadAttendanceLogs,
    loadAttendanceLogQuestions,
    loadProviders,
    loadSubsidyProgram,
    navigateAttendanceLogsPageTo,
    openAttendanceLogQuestionDialog,
    queueAttendanceLogChange,
    queueAttendanceLogQuestion,
    queueOperatingLogChange,
    updateAttendanceLog,
    updateAttendanceLogsHours,
    updateOperatingLog,
    updateAttendanceLogsAnswers,
    updateAttendanceLogsStatus,
    wasPresentOnDay,
  };
}
